import { Component, Element, Event, EventEmitter, h, Host, Prop } from "@stencil/core";

@Component({
    tag: "ux-tag",
    styleUrl: "tag.scss",
    shadow: true
})
export class Tag {
    /**
     * Primary element reference
     */
    @Element() domNode!: HTMLUxTagElement;

    /**
     * Control visibility of closing x icon & enable uxRemove Event
     * @default false
     */
    @Prop({ reflect: true }) readonly removable: boolean = false;

    /**
     * Style variant of the tag
     */
    @Prop({ reflect: true }) readonly variant: "default" | "secondary" | "inactive" = "default";

    /**
     * If set to true, the tag does not change it's appearance when hovered.
     */
    @Prop({ reflect: true }) readonly disabled: boolean = false;

    /**
     * If set to true, the tag is styled with the interactive variant.
     * An interactive tag has a pointer cursor, is focusable and can be accessed via keyboard.
     */
    @Prop({ reflect: true }) readonly interactive: boolean = false;

    /**
     * Display a leading icon
     */
    @Prop() readonly icon: string | undefined;

    /**
     * Event emitted when tag button pressed if removable attribute present
     */
    @Event() private uxRemove!: EventEmitter;

    private handleClickRemove = (event: Event) => {
        event.stopPropagation();
        this.uxRemove.emit();
    };

    private handleKeyDown = (event: KeyboardEvent) => {
        switch (event.key) {
            case "Enter":
                if (this.removable) {
                    event.stopPropagation();
                    this.uxRemove.emit();
                }
                break;
            case " ":
                event.preventDefault();
                if (this.removable) {
                    event.stopPropagation();
                    this.uxRemove.emit();
                }
                break;
            default:
                break;
        }
    };

    render() {
        const { removable, interactive, variant, disabled } = this;
        const isButton = (removable || interactive) && !disabled;
        return (
            <Host
                class={{
                    [`ux-tag--removable`]: removable,
                    [`ux-tag--interactive`]: interactive,
                    [`ux-tag--${variant}`]: variant !== "default"
                }}
                variant={variant}
                onClick={removable ? this.handleClickRemove : undefined}
                onKeyDown={isButton ? this.handleKeyDown : undefined}
                role={isButton ? "button" : undefined}
                tabindex={isButton ? 0 : undefined}
            >
                <slot name="extra" />
                {this.icon && <ux-icon name={this.icon} aria-hidden="true" aria-label="icon" weight="normal" />}
                <span class="ux-tag__label">
                    <slot />
                </span>
                {/*
                    ToDo: The removable status must be checked from the accessibility.
                */}
                {removable && <ux-icon name="cross" aria-hidden="true" aria-label="removable" weight="normal" />}
            </Host>
        );
    }
}
